import { useSSA } from './ssa/store';
/**
 * Headless runtime for the loader: loads the network dataset, feeds the SGP4
 * worker, runs the analysis when its inputs change, drives the live AOS/LOS
 * tick from the shared clock and polls D1 for multi-device sync. Mounted once
 * by the Shell so the log and analysis keep running in every mode.
 */
import { useEffect } from 'react';
import { usePassPlayback } from './passes/usePassPlayback';
import { useMissionReplay } from './replay';
import { clockNow, useClock } from '@/state/store';
import { toast } from '@/components/ui/toast';
import { computeRelayWindows, mergeRelayWindows, runAnalysis } from './analysis';
import { allocateOffThread } from './analysis/allocationRunner';
import { selectWeatherInput, useAwareness } from './awareness/store';
import { ANALYSIS, GUAM, SYNC_INTERVAL_MS } from './constants';
import { loaderApi } from './data/api';
import { loadNetworkDataset } from './data/loadNetwork';
import { getOrbitEngine, type EngineObserver } from './engine';
import { useLoader, type AnalysisProgress } from './store';
import { isDemoProfile, isFrozen } from '@/features/presenter';
import { loadFullCatalogue } from './ssa/data';
import type { Antenna, Mission, NetworkDataset } from './types';

const LIVE_TICK_MS = 750;
const REWIND_THRESHOLD_MS = 5000;
const ANALYSIS_DEBOUNCE_MS = 250;

export function toEngineMission(m: Mission) {
  return {
    id: m.id,
    tle1: m.orbit.tle?.line1 ?? '',
    tle2: m.orbit.tle?.line2 ?? '',
    omm: m.orbit.omm,
    bandType: m.requirements.bandType,
    bandTypes: m.requirements.bandTypes,
  };
}

export function toEngineAntenna(a: Antenna) {
  return {
    id: a.id,
    lat: a.location.lat,
    lon: a.location.lon,
    elevationMinDeg: a.coverage.elevationMinDeg ?? ANALYSIS.MIN_ELEVATION_DEG,
    bands: a.capabilities.bands,
  };
}

export function toEngineObserver(a: Antenna): EngineObserver {
  return {
    id: a.id,
    lat: a.location.lat,
    lon: a.location.lon,
    elevationMinDeg: a.coverage.elevationMinDeg ?? ANALYSIS.MIN_ELEVATION_DEG,
    elevationMaxDeg: a.coverage.elevationMaxDeg,
    zenithExclusionDeg: a.coverage.zenithExclusionDeg,
    maxSlewRateDegPerSec: a.coverage.maxSlewRateDegPerSec ?? a.capabilities.maxSlewRateDegPerSec,
    bands: a.capabilities.bands,
    mask: a.coverage.mask,
  };
}

export function hasTle(m: Mission): boolean {
  return m.orbit?.tleStatus !== 'unverified' && Boolean(m.orbit?.tle?.line1 && m.orbit.tle.line2);
}

let analysisSeq = 0;

export function LoaderRuntime() {
  useMissionReplay();
  usePassPlayback();
  const network = useLoader((s) => s.network);
  const retryDataset = useLoader((s) => s.datasetRetry);
  const dataset = useLoader((s) => s.dataset);
  const analysisRequest = useLoader((s) => s.analysisRequest);
  const activeAntennaIds = useLoader((s) => s.activeAntennaIds);
  const visibleAntennaIds = useLoader((s) => s.visibleAntennaIds);

  // Dataset per network.
  useEffect(() => {
    const ctrl = new AbortController();
    useLoader.getState().setDatasetLoading();
    loadNetworkDataset(network, {
      signal: ctrl.signal,
      forceStatic: isDemoProfile(),
      onNotice: (n) => toast(n.level === 'error' ? 'error' : n.level === 'warn' ? 'warn' : 'info', n.text),
    })
      .then((ds) => {
        if (!ctrl.signal.aborted) useLoader.getState().setDataset(ds);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        const message = err instanceof Error ? err.message : String(err);
        console.error('[loader] dataset load failed:', err);
        useLoader.getState().setDatasetError(message);
        toast('error', `Could not load the ${network} network: ${message}`);
      });
    return () => ctrl.abort();
  }, [network, retryDataset]);

  // Revalidate the shared server snapshot, never initiate an upstream refresh per browser.
  useEffect(() => {
    if (network !== 'full_catalogue' || isDemoProfile()) return;
    const ctrl = new AbortController();
    let busy = false;
    const timer = setInterval(async () => {
      if (busy || document.hidden || useSSA.getState().replayMode) return;
      busy = true;
      try {
        const next = await loadFullCatalogue({ signal: ctrl.signal });
        const current = useLoader.getState();
        if (!ctrl.signal.aborted && current.network === network && (next.catalogueSnapshot?.revision !== current.dataset?.catalogueSnapshot?.revision || next.source !== current.dataset?.source)) current.setDataset(next);
      } catch { /* Keep the validated snapshot and its visible age. */ }
      finally { busy = false; }
    }, 300000);
    return () => { ctrl.abort(); clearInterval(timer); };
  }, [network]);

  // SGP4 worker: every mission with a TLE, plus band tags for the live tick.
  useEffect(() => {
    if (!dataset) return;
    let cancelled = false;
    const engine = getOrbitEngine();
    const sats = dataset.missions.filter((m) => hasTle(m) || m.orbit.omm).map(toEngineMission);
    (async () => {
      await engine.init(sats);
      if (cancelled) return;
      await engine.setSatBands(
        dataset.missions.map((m) => ({
          id: m.id,
          bandType: m.requirements.bandType,
          bandTypes: m.requirements.bandTypes,
        })),
      );
    })().catch((err: unknown) => {
      console.error('[loader] orbit engine init failed:', err);
      toast('error', 'Orbit propagation failed to start; see the console.');
    });
    return () => {
      cancelled = true;
    };
  }, [dataset]);

  // Live observers: the active (what-if) antennas, narrowed by the visibility filter.
  useEffect(() => {
    if (!dataset) return;
    const visible = visibleAntennaIds ? new Set(visibleAntennaIds) : null;
    const active = new Set(activeAntennaIds);
    const observers = dataset.antennas
      .filter((a) => dataset.network !== 'full_catalogue' && !a.capabilities.monitoringOnly && active.has(a.id) && (!visible || visible.has(a.id)))
      .map(toEngineObserver);
    const engine = getOrbitEngine();
    engine
      .setLiveObservers(observers)
      .then(() => useLoader.getState().resetLiveContacts())
      .catch((err: unknown) => console.error('[loader] setLiveObservers failed:', err));
  }, [dataset, activeAntennaIds, visibleAntennaIds]);

  // Analysis on demand (debounced; the latest request wins).
  useEffect(() => {
    if (analysisRequest === 0) return;
    const run = ++analysisSeq;
    const ctrl = new AbortController();
    const timer = window.setTimeout(async () => {
      const st = useLoader.getState();
      if (!st.dataset) return;
      if (st.dataset.network === 'rfims' || st.dataset.network === 'full_catalogue') return;
      const activeM = new Set(st.activeMissionIds);
      const activeA = new Set(st.activeAntennaIds);
      const missions = st.dataset.missions.filter((m) => activeM.has(m.id) && !m.isCatalog);
      const antennas = st.dataset.antennas.filter((a) => !a.capabilities.monitoringOnly && activeA.has(a.id));
      if (missions.length === 0 || antennas.length === 0) {
        st.setAnalysisError('Select at least one mission and one antenna.');
        return;
      }
      if (missions.length > ANALYSIS.ANALYSIS_SOFT_CAP)
        toast('warn', `Analysing ${missions.length} missions; this can take a while.`);
      st.setAnalysisRunning();
      const engine = getOrbitEngine();
      try {
        const results = await runAnalysis({
          missions,
          antennas,
          startMs: st.timeframe.startMs,
          endMs: st.timeframe.endMs,
          isToggleEnabled: (id: string) => useLoader.getState().toggles[id] ?? true,
          weather: selectWeatherInput(),
          allocate: input => allocateOffThread(input, ctrl.signal),
          computeContactWindows: (
            ms: Mission[],
            as: Antenna[],
            startMs: number,
            endMs: number,
            onProgress?: (p: AnalysisProgress) => void,
          ) =>
            engine.computeContactWindows({
              missions: ms.filter(hasTle).map(toEngineMission),
              antennas: as.map(toEngineAntenna),
              startMs,
              endMs,
              onProgress,
            }),
          onProgress: (p: AnalysisProgress) => {
            if (run === analysisSeq) useLoader.getState().setAnalysisProgress(p);
          },
        });
        if (run !== analysisSeq || ctrl.signal.aborted) return;
        const latest = useLoader.getState();
        if (
          latest.tdrssShown &&
          (latest.toggles.tdrss_relay ?? true) &&
          latest.dataset &&
          latest.dataset.tdrss.length > 0
        ) {
          const relay = computeRelayWindows(
            latest.dataset.tdrss.filter((t) => t.enabled !== false),
            missions,
            st.timeframe.startMs,
            st.timeframe.endMs,
          );
          // Relay windows are keyed by TDRS id next to the antennas; they do not affect SLA (original rule).
          mergeRelayWindows(results.contactWindows, relay);
        }
        latest.setAnalysisResults(results);
      } catch (err: unknown) {
        if (run !== analysisSeq || ctrl.signal.aborted) return;
        const message = err instanceof Error ? err.message : String(err);
        console.error('[loader] analysis failed:', err);
        useLoader.getState().setAnalysisError(message);
        toast('error', `Analysis failed: ${message}`);
      }
    }, ANALYSIS_DEBOUNCE_MS);
    return () => { window.clearTimeout(timer); ctrl.abort(); };
  }, [analysisRequest, network]);

  // New live weather impacts re-run the analysis (they shorten or drop windows).
  useEffect(() => {
    let last = useAwareness.getState().weatherImpactsMs;
    return useAwareness.subscribe((s) => {
      if (s.weatherImpactsMs === last) return;
      last = s.weatherImpactsMs;
      const st = useLoader.getState();
      if (st.datasetStatus === 'ready') st.requestAnalysis();
    });
  }, []);

  // Live AOS/LOS tick on the shared clock, with rewind detection.
  useEffect(() => {
    let busy = false;
    let lastMs = clockNow();
    const id = window.setInterval(async () => {
      if (busy) return;
      const st = useLoader.getState();
      if (st.datasetStatus !== 'ready' || st.network === 'full_catalogue' || isFrozen()) return;
      const now = clockNow();
      busy = true;
      if (now < lastMs - REWIND_THRESHOLD_MS) {
        st.trimAosLosAfter(now);
        try {
          await getOrbitEngine().resetLiveContacts();
        } catch (err) {
          console.error('[loader] resetLiveContacts failed:', err);
        }
      }
      lastMs = now;
      try {
        const tick = await getOrbitEngine().liveTick(now);
        // A response from the previous network must not repopulate its contacts.
        if (useLoader.getState().dataset === st.dataset) useLoader.getState().applyLiveTick(tick);
      } catch (err) {
        console.error('[loader] live tick failed:', err);
      } finally {
        busy = false;
      }
    }, LIVE_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // Clock jumps of an hour or more move the analysis window to the new time and re-run (1.5 s debounce).
  useEffect(() => {
    let timer: number | null = null;
    let last = clockNow();
    const unsub = useClock.subscribe((s) => {
      const now = s.live ? Date.now() : s.simTime;
      const delta = Math.abs(now - last);
      last = now;
      if (s.playbackRange) return;
      if (delta < 3_600_000) return;
      const st = useLoader.getState();
      if (!st.autoRerunOnJump || st.datasetStatus !== 'ready') return;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => useLoader.getState().setTimeframeStart(clockNow()), 1500);
    });
    return () => {
      unsub();
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  // D1 sync polling (API-backed datasets only).
  useEffect(() => {
    if (!dataset || dataset.source !== 'api') return;
    let since = new Date().toISOString();
    const id = window.setInterval(async () => {
      if (isFrozen()) return;
      try {
        const res = await loaderApi.sync(since, network);
        since = res.serverTime;
        if (!res.hasChanges) return;
        const tables = new Set(res.changes.map((c) => c.table_name));
        const patch: Partial<NetworkDataset> = {};
        if (tables.has('antennas')) {
          const fetched = await loaderApi.antennas(network);
          // Antennas that exist only in this session (placed dishes, the Guam demo station) survive a sync.
          const local = (useLoader.getState().dataset?.antennas ?? []).filter(
            (a) =>
              (a.network === 'Placed' || a.id === GUAM.STATION_ID) && !fetched.some((f) => f.id === a.id),
          );
          patch.antennas = [...fetched, ...local];
        }
        if (tables.has('missions')) patch.missions = await loaderApi.missions(network);
        if (tables.has('priority_tiers')) patch.tiers = await loaderApi.priorityTiers(network);
        if (tables.has('feature_toggles')) patch.toggles = await loaderApi.featureToggles();
        if (tables.has('tdrss')) patch.tdrss = await loaderApi.tdrss(network);
        if (tables.has('scenarios')) patch.scenarios = await loaderApi.scenarios(network);
        if (Object.keys(patch).length > 0) {
          useLoader.getState().patchDataset(patch);
          toast('info', `Network data updated (${[...tables].join(', ')}).`);
        }
      } catch (err) {
        console.warn('[loader] sync poll failed:', err);
      }
    }, SYNC_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [dataset, network]);

  return null;
}
